interface RiskItem {
  id: string; risk: string; likelihood: number; impact: number; category?: string; source?: string; mitigation?: string
}
interface RiskMatrixData { title?: string; risks: RiskItem[] }

function score(l: number, i: number) { return l * i }
function colour(s: number) { return s >= 15 ? '#dc2626' : s >= 10 ? '#f97316' : s >= 5 ? '#eab308' : '#22c55e' }
function level(s: number) { return s >= 15 ? 'Critical' : s >= 10 ? 'High' : s >= 5 ? 'Medium' : 'Low' }

export function InlineRiskMatrix({ data }: { data: RiskMatrixData }) {
  const sorted = [...data.risks].sort((a, b) => score(b.likelihood, b.impact) - score(a.likelihood, a.impact))

  // 5x5 grid
  const grid: RiskItem[][][] = Array.from({ length: 5 }, () => Array.from({ length: 5 }, () => []))
  data.risks.forEach(r => {
    if (r.likelihood >= 1 && r.likelihood <= 5 && r.impact >= 1 && r.impact <= 5)
      grid[5 - r.likelihood][r.impact - 1].push(r)
  })

  const IMP = ['', 'Negligible', 'Minor', 'Moderate', 'Major', 'Catastrophic']

  return (
    <div className="my-2 rounded-lg overflow-hidden border border-gray-200 text-[10px]">
      {data.title && <div className="bg-gray-50 px-2 py-1 font-semibold text-gray-600 border-b text-xs">{data.title}</div>}
      {/* Grid */}
      <div className="p-2">
        <div className="grid grid-cols-5 gap-px">
          {grid.map((row, ri) => row.map((cell, ci) => {
            const bg = colour((5 - ri) * (ci + 1))
            return (
              <div key={`${ri}-${ci}`} className="aspect-square rounded-sm flex items-center justify-center"
                style={{ background: bg + '22', border: `1px solid ${bg}44` }}>
                {cell.map((r, idx) => (
                  <span key={idx} className="w-3 h-3 rounded-full text-white flex items-center justify-center font-bold"
                    style={{ background: colour(score(r.likelihood, r.impact)), fontSize: 6 }}>{r.id}</span>
                ))}
              </div>
            )
          }))}
        </div>
        <div className="flex justify-between mt-0.5">
          {IMP.slice(1).map(l => <span key={l} className="text-gray-400 text-center flex-1" style={{ fontSize: 7 }}>{l}</span>)}
        </div>
      </div>
      {/* Table */}
      <table className="w-full border-t">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-1.5 py-1 text-left text-gray-600">ID</th>
            <th className="px-1.5 py-1 text-left text-gray-600">Risk</th>
            <th className="px-1.5 py-1 text-center text-gray-600">L</th>
            <th className="px-1.5 py-1 text-center text-gray-600">I</th>
            <th className="px-1.5 py-1 text-center text-gray-600">Score</th>
            <th className="px-1.5 py-1 text-left text-gray-600">Level</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => {
            const s = score(r.likelihood, r.impact); const c = colour(s)
            return (
              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                <td className="px-1.5 py-1 font-bold" style={{ color: c }}>{r.id}</td>
                <td className="px-1.5 py-1 text-gray-800">{r.risk}</td>
                <td className="px-1.5 py-1 text-center text-gray-500">{r.likelihood}</td>
                <td className="px-1.5 py-1 text-center text-gray-500">{r.impact}</td>
                <td className="px-1.5 py-1 text-center font-bold" style={{ color: c }}>{s}</td>
                <td className="px-1.5 py-1"><span className="px-1 py-0.5 rounded text-white font-semibold" style={{ background: c, fontSize: 8 }}>{level(s)}</span></td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
